/**
 * The obligations setup of each entity, as the settings page (Contábil & Fiscal → Obrigações)
 * reads it, and the one rule that says when an entity is set up. The server filters and sorts by
 * this rule; the page shows it.
 *
 * `GET /fiscal/obligations/setup` (the listing), `GET /fiscal/obligations/setup/:entityId` (one
 * row, for the wizard), `GET /fiscal/obligations/setup/copy-targets` and
 * `POST /fiscal/obligations/setup/copy`.
 */
export const OBLIGATION_SETUP_ROLES = ['HEAD_OFFICE', 'BRANCH', 'NO_TAXPAYER'] as const;
export type ObligationSetupRole = (typeof OBLIGATION_SETUP_ROLES)[number];

export interface ObligationSetupRow {
  entityId: string;
  name: string;
  cnpj: string | null;
  role: ObligationSetupRole;
  taxpayerId: string | null;
  /** Questions without an answer; a branch counts the company ones its head office left open. */
  screening: { unanswered: number; total: number };
  /** Obligations listed on this entity (a head office lists the federal ones) still pending. */
  decisions: { pending: number; total: number };
  /**
   * Applicable obligations listed on this entity, with periods, that have no fixed day in the
   * catalog and no deadline rule recorded for their scope.
   */
  deadlines: { missing: number };
  /** Applicable obligations with a step nobody holds. */
  responsibles: { missing: number };
  /** The company accountant in force; a branch reads its head office's. */
  accountant: { name: string; crc: string; validFrom: string } | null;
  /** Applicable obligations listed on this entity whose delivery channel is ready today. */
  channels: { active: number };
  /**
   * True once anything of the setup was saved for this entity's own scope: a screening answer, a
   * decision, a deadline rule, a responsible, a channel or (a head office) the company's accountant.
   * A branch is not started by what its head office saved.
   */
  started: boolean;
}

/**
 * One page of the listing. The server filters, sorts and pages: `search`, `status` and `role`
 * (comma-separated choices), `sortBy` (`entity` | `status`), `page`, `limit`. The counts are per
 * status over the same search and filters, without the status one, for the view tabs.
 */
export interface ObligationSetupResponse {
  asOf: string;
  items: ObligationSetupRow[];
  total: number;
  counts: Record<ObligationSetupStatus | 'all', number>;
}

/** The entities that can receive a copy from a source: every other one with a taxpayer. */
export interface ObligationSetupCopyTargetsResponse {
  items: ObligationSetupRow[];
}

/**
 * Copies the screening answers only: they are facts about the company. The responsible people and
 * the accountant are often different per entity, so each entity sets its own.
 */
export interface CopyObligationSetupRequest {
  sourceEntityId: string;
  targetEntityIds: string[];
}

export const OBLIGATION_SETUP_STEPS = [
  'screening',
  'decisions',
  'deadlines',
  'responsibles',
  'accountant',
  'channels',
] as const;
export type ObligationSetupStep = (typeof OBLIGATION_SETUP_STEPS)[number];

export const OBLIGATION_SETUP_STATUSES = ['NOT_STARTED', 'IN_PROGRESS', 'READY'] as const;
export type ObligationSetupStatus = (typeof OBLIGATION_SETUP_STATUSES)[number];

/** Steps the status ignores: an entity can be set up before any power of attorney is recorded. */
export const OPTIONAL_SETUP_STEPS: readonly ObligationSetupStep[] = ['channels'];

const REQUIRED_STEPS: readonly ObligationSetupStep[] = OBLIGATION_SETUP_STEPS.filter(
  (step) => !OPTIONAL_SETUP_STEPS.includes(step),
);

export function isStepDone(row: ObligationSetupRow, step: ObligationSetupStep): boolean {
  switch (step) {
    case 'screening':
      return row.screening.unanswered === 0;
    case 'decisions':
      return row.decisions.pending === 0;
    case 'deadlines':
      return row.deadlines.missing === 0;
    case 'responsibles':
      return row.responsibles.missing === 0;
    case 'accountant':
      return row.accountant !== null;
    case 'channels':
      return row.channels.active > 0;
  }
}

/**
 * Set up = every required step done: the screening answered, no pending decision, every municipal
 * deadline recorded, every step of every applicable obligation held, and an accountant in force.
 */
export function setupStatus(row: ObligationSetupRow): ObligationSetupStatus {
  if (REQUIRED_STEPS.every((step) => isStepDone(row, step))) return 'READY';
  return row.started ? 'IN_PROGRESS' : 'NOT_STARTED';
}

/** Where the wizard resumes: the first required step that is not done. */
export function firstOpenStep(row: ObligationSetupRow): ObligationSetupStep {
  return REQUIRED_STEPS.find((step) => !isStepDone(row, step)) ?? 'screening';
}

export function isObligationSetupStep(value: string | null): value is ObligationSetupStep {
  return (OBLIGATION_SETUP_STEPS as readonly string[]).includes(value ?? '');
}
